import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

import {
  Business,
  CheckCircle,
  Warning,
  Schedule,
  TrendingUp,
  Search,
  ViewModule,
  ViewList,
} from '@mui/icons-material';
import { useCompany } from '../contexts/CompanyContext';
import { Company, analyticsAPI } from '../services/api';

const CompanySelector: React.FC = () => {
  const navigate = useNavigate();
  const { clientCompanies, consultantCompanies, isLoading, userPermissions } = useCompany();
  
  // Get all client companies from consultant companies and direct client companies
  // Deduplicate by company ID to avoid showing the same company twice
  const allClientCompanies = React.useMemo(() => {
    const clientsMap = new Map<number, Company>();
    
    // Add direct client companies
    clientCompanies.forEach(company => {
      clientsMap.set(company.id, company);
    });
    
    // Add client companies from consultant companies
    consultantCompanies.forEach(consultant => {
      if (consultant.client_companies) {
        consultant.client_companies.forEach(company => {
          if (!clientsMap.has(company.id)) {
            clientsMap.set(company.id, company);
          }
        });
      }
    });
    
    return Array.from(clientsMap.values());
  }, [clientCompanies, consultantCompanies]);

  // Real company statuses and analytics from API
  const [companyStatuses, setCompanyStatuses] = useState<Record<number, any>>({});
  const [companyAnalytics, setCompanyAnalytics] = useState<Record<number, any>>({});
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);
  
  // Table view state
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [orderBy, setOrderBy] = useState<'name' | 'health' | 'audits' | 'tasks' | 'lastAudit'>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  // Load real company statuses
  useEffect(() => {
    const loadCompanyStatuses = async () => {
      if (allClientCompanies.length === 0) return;
      
      try {
        setIsLoadingStatuses(true);
        
        // Load status and analytics for each company
        const dataPromises = allClientCompanies.map(company =>
          Promise.all([
            analyticsAPI.getCompanyStatus(company.id),
            analyticsAPI.getCompanyAnalytics(company.id)
          ])
            .then(([status, analytics]) => ({ companyId: company.id, status, analytics }))
            .catch(error => {
              console.warn(`Failed to load data for company ${company.id}:`, error);
              return {
                companyId: company.id,
                status: {
                  status: 'unknown',
                  status_color: 'info',
                  status_icon: 'business'
                },
                analytics: {
                  health_score: 75,
                  overdue_audits: 0,
                  in_progress_audits: 0,
                  open_tasks: 0,
                  recent_activity: []
                }
              };
            })
        );
        
        const results = await Promise.all(dataPromises);
        const statusMap = results.reduce((acc, { companyId, status }) => {
          acc[companyId] = status;
          return acc;
        }, {} as Record<number, any>);
        
        const analyticsMap = results.reduce((acc, { companyId, analytics }) => {
          acc[companyId] = analytics;
          return acc;
        }, {} as Record<number, any>);
        
        setCompanyStatuses(statusMap);
        setCompanyAnalytics(analyticsMap);
        console.log('✅ Company statuses loaded:', statusMap);
        console.log('✅ Company analytics loaded:', analyticsMap);
        
      } catch (error) {
        console.error('❌ Error loading company statuses:', error);
      } finally {
        setIsLoadingStatuses(false);
      }
    };

    loadCompanyStatuses();
  }, [allClientCompanies]);

  const getCompanyStatusColor = (company: Company) => {
    const status = companyStatuses[company.id];
    return status?.status_color || 'info';
  };

  const getStatusText = (company: Company) => {
    const status = companyStatuses[company.id];
    if (!status) return 'Loading...';
    
    switch (status.status) {
      case 'healthy': return 'Healthy';
      case 'warning': return 'Warning';
      case 'critical': return 'Critical';
      case 'overdue': return 'Overdue';
      case 'unknown': return 'Unknown';
      default: return 'Unknown';
    }
  };

  const getLastAuditText = (company: Company) => {
    const analytics = companyAnalytics[company.id];
    if (!analytics) return 'Loading...';
    
    const activity = analytics.recent_activity || [];
    const lastCompletedAudit = activity.find((a: any) => a.type === 'audit_completed');
    
    if (lastCompletedAudit) {
      const days = Math.floor((new Date().getTime() - new Date(lastCompletedAudit.date).getTime()) / (1000 * 60 * 60 * 24));
      return `${days} days ago`;
    }
    
    return 'No recent audits';
  };

  const getHealthScore = (company: Company) => {
    const analytics = companyAnalytics[company.id];
    // Return null if health_score is null/undefined, otherwise return the score (could be 0)
    return analytics?.health_score !== null && analytics?.health_score !== undefined 
      ? analytics.health_score 
      : null;
  };

  const getOpenTasks = (company: Company) => {
    const analytics = companyAnalytics[company.id];
    return analytics?.open_tasks || 0;
  };

  const getOpenAudits = (company: Company) => {
    const analytics = companyAnalytics[company.id];
    return analytics?.in_progress_audits || 0;
  };

  const getStatusIcon = (company: Company) => {
    const status = companyStatuses[company.id];
    if (!status) return <Business />;
    
    switch (status.status_icon) {
      case 'check_circle': return <CheckCircle />;
      case 'schedule': return <Schedule />;
      case 'warning': return <Warning />;
      default: return <Business />;
    }
  };

  const handleCompanySelect = (companyId: number) => {
    navigate(`/company/${companyId}`);
  };

  // Sorting and filtering logic
  const filteredAndSortedCompanies = useMemo(() => {
    let filtered = allClientCompanies;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(company => 
        company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        company.city?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(company => {
        const status = companyStatuses[company.id];
        return status?.status === statusFilter;
      });
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (orderBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'health':
          aValue = getHealthScore(a);
          bValue = getHealthScore(b);
          break;
        case 'audits':
          aValue = getOpenAudits(a);
          bValue = getOpenAudits(b);
          break;
        case 'tasks':
          aValue = getOpenTasks(a);
          bValue = getOpenTasks(b);
          break;
        case 'lastAudit':
          const aAnalytics = companyAnalytics[a.id];
          const bAnalytics = companyAnalytics[b.id];
          const aActivity = aAnalytics?.recent_activity?.find((act: any) => act.type === 'audit_completed');
          const bActivity = bAnalytics?.recent_activity?.find((act: any) => act.type === 'audit_completed');
          aValue = aActivity ? new Date(aActivity.date).getTime() : 0;
          bValue = bActivity ? new Date(bActivity.date).getTime() : 0;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [allClientCompanies, searchQuery, statusFilter, orderBy, order, companyStatuses, companyAnalytics]);

  const handleSort = (property: typeof orderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading companies...
        </Typography>
      </Container>
    );
  }

  if (allClientCompanies.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">
          No client companies found. Please contact your administrator.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Select Company
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Choose a client company to view their dashboard and manage audits
          </Typography>
        </Box>
        
        {/* View Toggle */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Card View">
            <IconButton 
              onClick={() => setViewMode('card')}
              color={viewMode === 'card' ? 'primary' : 'default'}
            >
              <ViewModule />
            </IconButton>
          </Tooltip>
          <Tooltip title="Table View">
            <IconButton 
              onClick={() => setViewMode('table')}
              color={viewMode === 'table' ? 'primary' : 'default'}
            >
              <ViewList />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Search and Filter Controls */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search companies..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            label="Status Filter"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="healthy">Healthy</MenuItem>
            <MenuItem value="warning">In Progress</MenuItem>
            <MenuItem value="overdue">Overdue</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table View */}
      {viewMode === 'table' && (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Company
                  </TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Location</TableCell>
                <TableCell align="center">Size</TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'health'}
                    direction={orderBy === 'health' ? order : 'asc'}
                    onClick={() => handleSort('health')}
                  >
                    Health Score
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'audits'}
                    direction={orderBy === 'audits' ? order : 'asc'}
                    onClick={() => handleSort('audits')}
                  >
                    Open Audits
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'tasks'}
                    direction={orderBy === 'tasks' ? order : 'asc'}
                    onClick={() => handleSort('tasks')}
                  >
                    Open Tasks
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'lastAudit'}
                    direction={orderBy === 'lastAudit' ? order : 'asc'}
                    onClick={() => handleSort('lastAudit')}
                  >
                    Last Audit
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedCompanies.map((company) => (
                <TableRow
                  key={company.id}
                  data-testid="company-row"
                  data-company-id={company.id}
                  hover
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => handleCompanySelect(company.id)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business color="primary" />
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {company.name}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(company)}
                      label={getStatusText(company)}
                      color={getCompanyStatusColor(company)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{company.city}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {company.employee_count} emp • {company.sites_count ?? company.site_count ?? 0} sites
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {getHealthScore(company) !== null ? (
                      <Typography 
                        variant="body1" 
                        fontWeight="bold"
                        color={
                          getHealthScore(company)! >= 75 ? 'success.main' :
                          getHealthScore(company)! >= 50 ? 'warning.main' :
                          'error.main'
                        }
                      >
                        {getHealthScore(company)}%
                      </Typography>
                    ) : (
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                      >
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={getOpenAudits(company)} 
                      color="info" 
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={getOpenTasks(company)} 
                      color={getOpenTasks(company) > 0 ? 'warning' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">
                      {getLastAuditText(company)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<TrendingUp />}
                      data-testid="view-company-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCompanySelect(company.id);
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              
              {filteredAndSortedCompanies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No companies found matching your search criteria
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Card View (Original) */}
      {viewMode === 'card' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 3 }}>
          {filteredAndSortedCompanies.map((company) => (
            <Paper
              key={company.id}
              data-testid="company-card"
              data-company-id={company.id}
              elevation={2}
              sx={{ 
                p: 3,
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                },
              }}
              onClick={() => handleCompanySelect(company.id)}
            >
              {/* Company Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Business sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h2" noWrap>
                    {company.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {company.city}
                  </Typography>
                </Box>
              </Box>

              {/* Status Chip */}
              <Box sx={{ mb: 2 }}>
                <Chip
                  icon={getStatusIcon(company)}
                  label={getStatusText(company)}
                  color={getCompanyStatusColor(company)}
                  variant="outlined"
                  size="small"
                />
              </Box>

              {/* Company Details */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {company.employee_count} employees • {company.sites_count ?? company.site_count ?? 0} sites
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  Last audit: {getLastAuditText(company)}
                </Typography>
              </Box>

              {/* Quick Stats */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Health Score
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {getHealthScore(company)}%
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Open Audits
                  </Typography>
                  <Typography variant="h6" color="info.main">
                    {getOpenAudits(company)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Open Tasks
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {getOpenTasks(company)}
                  </Typography>
                </Box>
              </Box>

              <Button 
                size="small" 
                startIcon={<TrendingUp />}
                fullWidth
                variant="outlined"
                data-testid="view-dashboard-button"
                sx={{ mt: 2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCompanySelect(company.id);
                }}
              >
                View Dashboard
              </Button>
            </Paper>
          ))}
          
          {filteredAndSortedCompanies.length === 0 && (
            <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No companies found matching your search criteria
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Results count */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredAndSortedCompanies.length} of {allClientCompanies.length} companies
        </Typography>
      </Box>

      {/* Admin Access */}
      {userPermissions?.can_manage_clients && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin/companies')}
            startIcon={<Business />}
          >
            Manage Companies
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default CompanySelector; 